import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';

interface StatItem {
  id: string;
  date: string;
  deposit: number;
  depositCount: number;
  topup: number;
  topupCount: number;
  withdraw: number;
  withdrawCount: number;
  settle: number;
  settleCount: number;
  hold: number;
  feeSeller: number;
  feePlatform: number;
  fee: number;
  myPayinShare: number;
  myPayoutShare: number;
  payinSharePercent: number;
  payoutSharePercent: number;
}

interface MerchantStatsResponse {
  items: StatItem[];
  total: number;
  payinSharePercent: number;
  payoutSharePercent: number;
  merchant: { id: string; code: string; name: string };
}

function getMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    options.push({ value, label });
  }
  return options;
}

function padDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getDateRangeForMonth(yearMonth: string): { dateFrom: string; dateTo: string } {
  const [year, month] = yearMonth.split('-').map(Number);
  const lastDay = new Date(year, month, 0);
  const now = new Date();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const endDate = isCurrentMonth ? now : lastDay;
  return { dateFrom: `${yearMonth}-01`, dateTo: padDate(endDate) };
}

export function SubSellerMerchantStats() {
  const { t } = useTranslation();
  const { merchantId } = useParams<{ merchantId: string }>();
  const navigate = useNavigate();

  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const monthOptions = getMonthOptions();
  const { dateFrom, dateTo } = getDateRangeForMonth(selectedMonth);

  const { data, isLoading } = useQuery({
    queryKey: ['sub-seller', 'merchant', merchantId, 'stats', dateFrom, dateTo],
    queryFn: () =>
      apiGet<MerchantStatsResponse>(`/sub-seller/merchants/${merchantId}/stats`, {
        dateFrom,
        dateTo,
        pageSize: '100',
      }),
    enabled: !!merchantId,
  });

  const stats = data?.ok ? data.data.items : [];
  const payinSharePercent = data?.ok ? data.data.payinSharePercent : 0;
  const payoutSharePercent = data?.ok ? data.data.payoutSharePercent : 0;
  const merchant = data?.ok ? data.data.merchant : null;

  const formatNum = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/sub-seller/merchants')}>
          ← {t('merchants.title')}
        </Button>
        <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
          {merchant ? `${merchant.name} (${merchant.code})` : t('merchants.merchantStatsTitle')}
        </h1>
      </div>

      {stats.length > 0 && (payinSharePercent > 0 || payoutSharePercent > 0) && (
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950">
            <div className="text-xs font-medium text-blue-600 dark:text-blue-400">
              {t('transactions.calcPayinFee')} ({payinSharePercent}%)
            </div>
            <div className="mt-1 text-xl font-bold text-blue-700 dark:text-blue-300">
              {formatNum(stats.reduce((s, r) => s + r.myPayinShare, 0))}
            </div>
            <div className="mt-0.5 text-xs text-blue-500 dark:text-blue-400">
              ({t('transactions.deposit')} + {t('transactions.topup')}) × {payinSharePercent}%
            </div>
          </div>
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
            <div className="text-xs font-medium text-green-600 dark:text-green-400">
              {t('transactions.calcPayoutFee')} ({payoutSharePercent}%)
            </div>
            <div className="mt-1 text-xl font-bold text-green-700 dark:text-green-300">
              {formatNum(stats.reduce((s, r) => s + r.myPayoutShare, 0))}
            </div>
            <div className="mt-0.5 text-xs text-green-500 dark:text-green-400">
              ({t('transactions.withdraw')} + {t('transactions.settle')}) × {payoutSharePercent}%
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('merchants.merchantStatsTitle')}</CardTitle>
            <div className="flex items-center gap-3">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {dateFrom} ~ {dateTo}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <div className="p-6 text-gray-400">{t('common.loading')}</div>}
          {!isLoading && stats.length === 0 && (
            <div className="p-6 text-gray-400">{t('merchants.statsEmptyHint')}</div>
          )}
          {stats.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('transactions.date')}</TableHead>
                    <TableHead className="text-right">{t('transactions.deposit')}</TableHead>
                    <TableHead className="text-right">{t('transactions.depositCount')}</TableHead>
                    <TableHead className="text-right">{t('transactions.topup')}</TableHead>
                    <TableHead className="text-right">{t('transactions.topupCount')}</TableHead>
                    <TableHead className="text-right">{t('transactions.withdraw')}</TableHead>
                    <TableHead className="text-right">{t('transactions.withdrawCount')}</TableHead>
                    <TableHead className="text-right">{t('transactions.settle')}</TableHead>
                    <TableHead className="text-right">{t('transactions.hold')}</TableHead>
                    <TableHead className="text-right text-blue-600 dark:text-blue-400">
                      {t('transactions.calcPayinFee')}
                    </TableHead>
                    <TableHead className="text-right text-green-600 dark:text-green-400">
                      {t('transactions.calcPayoutFee')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.map((stat) => (
                    <TableRow key={stat.id}>
                      <TableCell className="whitespace-nowrap font-mono text-sm">
                        {stat.date}
                      </TableCell>
                      <TableCell className="text-right">{formatNum(stat.deposit)}</TableCell>
                      <TableCell className="text-right text-gray-500">
                        {stat.depositCount}
                      </TableCell>
                      <TableCell className="text-right">{formatNum(stat.topup)}</TableCell>
                      <TableCell className="text-right text-gray-500">{stat.topupCount}</TableCell>
                      <TableCell className="text-right">{formatNum(stat.withdraw)}</TableCell>
                      <TableCell className="text-right text-gray-500">
                        {stat.withdrawCount}
                      </TableCell>
                      <TableCell className="text-right">{formatNum(stat.settle)}</TableCell>
                      <TableCell className="text-right">{formatNum(stat.hold)}</TableCell>
                      <TableCell className="text-right font-medium text-blue-600 dark:text-blue-400">
                        {formatNum(stat.myPayinShare)}
                      </TableCell>
                      <TableCell className="text-right font-medium text-green-600 dark:text-green-400">
                        {formatNum(stat.myPayoutShare)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2 border-gray-300 bg-gray-100 text-base font-bold dark:border-gray-600 dark:bg-gray-700">
                    <TableCell className="text-gray-900 dark:text-gray-100">
                      {t('transactions.total')}
                    </TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">
                      {formatNum(stats.reduce((s, r) => s + r.deposit, 0))}
                    </TableCell>
                    <TableCell className="text-right text-gray-600 dark:text-gray-300">
                      {stats.reduce((s, r) => s + r.depositCount, 0)}
                    </TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">
                      {formatNum(stats.reduce((s, r) => s + r.topup, 0))}
                    </TableCell>
                    <TableCell className="text-right text-gray-600 dark:text-gray-300">
                      {stats.reduce((s, r) => s + r.topupCount, 0)}
                    </TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">
                      {formatNum(stats.reduce((s, r) => s + r.withdraw, 0))}
                    </TableCell>
                    <TableCell className="text-right text-gray-600 dark:text-gray-300">
                      {stats.reduce((s, r) => s + r.withdrawCount, 0)}
                    </TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">
                      {formatNum(stats.reduce((s, r) => s + r.settle, 0))}
                    </TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">
                      {formatNum(stats.reduce((s, r) => s + r.hold, 0))}
                    </TableCell>
                    <TableCell className="text-right text-blue-600 dark:text-blue-400">
                      {formatNum(stats.reduce((s, r) => s + r.myPayinShare, 0))}
                    </TableCell>
                    <TableCell className="text-right text-green-600 dark:text-green-400">
                      {formatNum(stats.reduce((s, r) => s + r.myPayoutShare, 0))}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
